import { RoadmapService } from './roadmap.service';
import { SpreadsheetColumnsService } from './spreadsheet-columns.service';
import { Injectable } from '@angular/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { ColDef, ColumnGroupShowType } from 'ag-grid-community';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { first, map, startWith, switchMap, tap } from 'rxjs/operators';
import { APIService, KPI } from '../api';
import { AssetsService } from './assets.service';
import { DocumentTypesService } from './document-types.service';
import { InitiativesService } from './initiatives.service';
import { InvestmentCategoriesService } from './investment-categories.service';
import { InvestmentReasonsService } from './investment-reasons.service';
import { PerimetersService } from './perimeters.service';
import { ReferenceDataService } from './reference-data.service';
import { idMapToArray, ScopeService } from './scope.service';
import { YearsService } from "./years.service";
import { ActivatedRoute } from "@angular/router";
import { InvestmentScenariiService } from "./investment-scenarii.service";
import { IdNameMixin } from '../api/models';
import { ConfigService } from './config.service';
import { WorkflowService } from './workflow.service'
import { ExportsService } from './exports.service';
import { SELECTED_PLAN_ID } from './constants';
import { ProjectsService } from './projects.service';
import { QuotePartService } from './quote-part.service';
import { SettingsService } from './settings.service';
// Removed unused import


@Injectable()
export class SpreadsheetColumnsInvestmentsService extends SpreadsheetColumnsService {

  /**
   * store the value of options that're going to fetch with the investment table so that we can
   * access this from other services.
   * @private
   */
  private readonly currentInvestmentTableOptions = new BehaviorSubject<KPI.TServerSideGetRowsRequest>(null);

  constructor(
    protected documentTypesService: DocumentTypesService,
    protected investmentCategoriesService: InvestmentCategoriesService,
    protected investmentReasonsService: InvestmentReasonsService,
    protected scope: ScopeService,
    protected translate: TranslateService,
    protected api: APIService,
    protected perimetersService: PerimetersService,
    protected referenceDataService: ReferenceDataService,
    protected assetsService: AssetsService,
    protected yearsService: YearsService,
    protected route: ActivatedRoute,
    protected investmentScenariiService: InvestmentScenariiService,
    protected initiativesService: InitiativesService,
    protected roadmapService: RoadmapService,
    private projectsService: ProjectsService,
    private settingService: SettingsService,
    private configService: ConfigService,
    private workflowService: WorkflowService,
    private exportsService: ExportsService,
    protected config: ConfigService,
    protected quotePartService: QuotePartService,
  ) {
    super(
      documentTypesService, investmentCategoriesService, investmentReasonsService,
      scope, translate, api, perimetersService, referenceDataService, assetsService,
      yearsService, route, investmentScenariiService, initiativesService, roadmapService,
      quotePartService, config
    );
  }

  // Is main scenario locked from edition
  public isMainScenarioLocked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  /** IDs (as strings) of non-cancelled investment statuses — used to build the default filter */
  public nonCancelledStatusIds: string[] = [];
  /** Current main scenario ID — used to build the correct filter key `status_scenario_<id>` */
  public currentMainScenarioId: number = null;
  public toggleMainScenarioLock(): void {
    this.isMainScenarioLocked$.next(!this.isMainScenarioLocked$.value);
  }

  /**
   * SIMPLE INVESTMENTS COLUMNS
   */
  private agGridInvestmentColumns(
    showScenarii: boolean,
    forcedScenario: KPI.InvestmentScenario = null,
    isFidjiAct: boolean = false,
  ): Observable<KPI.CustomColGroupDef[]> {

    return combineLatest([
      this.scope.user$,
      this.investmentScenariiService.mainScenario$,
      this.scope.perimeterTypes$.pipe(idMapToArray()),
      this.scope.investmentStatuses$.pipe(first(), idMapToArray()),
      this.scope.getInvestFurtherInfo$.pipe(first(), idMapToArray()),
      this.scope.investmentPriorities$.pipe(idMapToArray()),
      this.scope.investmentTypes$.pipe(first(), idMapToArray()),
      this.scope.superCategories$.pipe(first(), idMapToArray()),
      this.scope.categories$.pipe(first(), idMapToArray()),
      this.scope.subCategories$.pipe(first(), idMapToArray()),
      this.scope.assetTypes$.pipe(first(), idMapToArray()),
      this.scope.investmentBudgetOrigins$.pipe(first(), idMapToArray()),
      this.scope.clusters$.pipe(first(), idMapToArray()),
      this.buildAgGridInvestmentReasonsColumns(false).pipe(first()),
      this.buildAgGridInvestmentCategoriesColumns(false).pipe(first()),
      this.buildAgGridDocumentTypesColumns(true).pipe(first()),
      this.buildAgGridPayersColumns(true).pipe(first()),
      // this.buildAgGridInvoicingsColumns(false).pipe(first()),
      this.buildAgGridRoadmapIndicatorColumns(false).pipe(first()),
      this.initiativesService.initiativeTypes$,
      this.scope.taxonomyObjectives$.pipe(first(), idMapToArray()),
      this.scope.user$.pipe(
        switchMap((user) => {
          if (user.can_access_workflow) {
            return combineLatest([
              this.workflowService.investmentPlans$,
              this.perimetersService.allPerimeters$.pipe(first())
            ]).pipe(
              switchMap(([plans, perimeters]) => {
                const activePlans = plans.filter(elt => !elt.closed);
                const storedPlan = parseInt(localStorage.getItem(SELECTED_PLAN_ID))
                const selectedPlan = storedPlan ? plans.find(plan => plan.id === storedPlan) : activePlans[0];
                return selectedPlan
                  ? this.workflowService.getRounds(selectedPlan, perimeters)
                  : of([]);
              })
            );
          } else {
            return of([]);
          }
        })
      ),
      this.investmentScenariiService.selectedScenarii$.pipe(
        map((scenarii) => scenarii.filter((s) => !!s)),
      ),
      this.projectsService.getAllProjects().pipe(first()),
      this.loadSiteAndPerimeters(),
      this.scope.config$,
      this.buildAgGridDetailOfPayerShare().pipe(first()),
      this.getAgGridCodesColumn(),
      this.initiativesService.relevantEnergyTypes$
    ]).pipe(
      map(([
        user,
        mainScenarioTmp,
        perimeterTypes,
        statuses,
        furtherInformations,
        priorities,
        investmentTypes,
        superCategories,
        categories,
        subCategories,
        assetTypes,
        budgetOrigins,
        clusters,
        invReasonsCols,
        invCategCols,
        docTypesCols,
        payersCols,
        // invoicingsCols,
        roadmapIndicatorsCols,
        initiativeTypes,
        taxonomyObjectives,
        workflowRounds,
        selectedScenariiTmp,
        projects,
        _,
        environmentConfig,
        detailOfPayeShareCols,
        codesCols,
        energyTypes
      ]) => {
        const selectedScenarii = selectedScenariiTmp as KPI.InvestmentScenario[];
        const mainScenario: KPI.InvestmentScenario = mainScenarioTmp as KPI.InvestmentScenario;
        this.currentMainScenarioId = mainScenario?.id ?? null;
        const can_access_energy = (user as KPI.User)?.can_access_energy || false;
        const isMainScenarioSelected = mainScenario && (selectedScenarii as KPI.InvestmentScenario[]).find((s) =>
          s.id === mainScenario.id
        );
        const isMainScenarioForced = (!!forcedScenario && forcedScenario.id !== mainScenario.id);
        const hideMainScenario = (!!showScenarii && !isMainScenarioSelected) || isMainScenarioForced;
        const quotePartEnabled = !!environmentConfig.quote_part_enabled;

        const sponsorsCols: KPI.CustomColGroupDef[] = [{
          headerName: this.translate.instant('pages.common.ag-grid.column_groups.payer'),
          icons: {
            columnGroupOpened: '<i class="far fa-minus"/>',
            columnGroupClosed: '<i class="far fa-plus"/>',
          },
          children: [
            {
              field: 'empty_payers',
              headerName: '',
              width: 90,
              hide: true,
              lockVisible: true,
              columnGroupShow: 'closed',
              menuTabs: [],
              suppressColumnsToolPanel: true,
              suppressFiltersToolPanel: true,
              filter: false,
            },
            ...(payersCols as KPI.CustomColDef[]).map((col) => ({
              ...col,
              columnGroupShow: 'open' as ColumnGroupShowType,
            })),
          ],
        }]
        const sponsorsOrQuotePart = quotePartEnabled
          ? detailOfPayeShareCols
          : sponsorsCols;

        (perimeterTypes as KPI.PerimeterType[]).forEach((perimeterType) => {
          this.perimeterTypesMap[perimeterType.id] = perimeterType.name;
        });
        this.nonCancelledStatusIds = (statuses as KPI.InvestmentStatus[])
          .filter(s => !s.is_cancelled && s.show_in_charts)
          .map(s => String(s.id));
        (statuses as KPI.InvestmentStatus[]).forEach((investmentStatus) => {
          this.investmentStatusesMap[investmentStatus.id] =
            investmentStatus.name;
        });
        (furtherInformations as IdNameMixin[]).forEach((investmentFurtherInformations) => {
          this.furtherInformationsMap[investmentFurtherInformations.id] =
            investmentFurtherInformations.name;
        });
        (priorities as IdNameMixin[]).forEach((priority) => {
          this.prioritiesMap[priority.id] = priority.name.toString();
        });
        (investmentTypes as KPI.InvestmentType[]).forEach((investmentType) => {
          this.investmentTypesMap[investmentType.id] = investmentType.name;
        });
        (superCategories as KPI.SuperCategory[]).forEach((superCategory) => {
          this.superCategoriesMap[superCategory.id] = superCategory.name;
        });
        (categories as KPI.Category[]).forEach((category) => {
          this.categoriesMap[category.id] = category.name;
        });
        (subCategories as KPI.SubCategory[]).forEach((subCategorie) => {
          this.subCategoriesMap[subCategorie.id] = subCategorie.name;
        });
        (assetTypes as KPI.AssetType[]).forEach((assetType) => {
          this.assetTypesMap[assetType.id] = assetType.name;
        });
        (initiativeTypes as KPI.InitiativeType[]).forEach((initiativeType) => {
          this.initiativeTypesMap[initiativeType.id] =
            initiativeType.name;
          if (initiativeType.parent !== null) {
            this.initiativeTypesLeafFriendlyMap[initiativeType.id] = initiativeType.friendly_name || initiativeType.name;
          }
        });
        (taxonomyObjectives as KPI.TaxonomyObjective[]).forEach((taxonomyObjective) => {
          this.taxonomyObjectivesMap[taxonomyObjective.id] =
            taxonomyObjective.name;
        });
        (workflowRounds as KPI.WorkflowRound[]).forEach((round) => {
          this.workflowRoundsMap[round.id] = round.name;
        });
        (projects as KPI.Project[]).forEach((project) => {
          this.projectsMap[project.id] = project.label;
        });
        (budgetOrigins as KPI.InvestmentBudgetOrigin[]).forEach((origin) => {
          this.budgetOriginsMap[origin.id] = origin.name;
        });
        (clusters as KPI.Cluster[]).forEach((cluster) => {
          this.clustersMap[cluster.id] = cluster.name;
        });
        (energyTypes as KPI.InitiativeEnergyType[]).forEach((energyType) => {
          this.energyTypesMap[energyType.id] = energyType.name;
        });
        return [
          // ROWS
          {
            headerName: this.translate.instant('core.utils.spreadsheet_columns.column_label'),
            marryChildren: true,
            headerClass: 'hide-ag-header-label',
            children: [
              {
                field: 'row_nb',
                headerName: '',
                rowDrag: false,
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                filter: false,
                resizable: false,
                sortable: false,
                menuTabs: [],
                width: 45,
                minWidth: 45,
                maxWidth: 45,
                pinned: true,
                lockPosition: true,
                lockVisible: true,
                hide: true,
                // enableCellChangeFlash: false,
                cellStyle: {
                  'text-align': 'center',
                  color: '#707171',
                },
                valueGetter: this.setRowNumber,
              },
              {
                field: 'invest_id',
                headerName: this.translate.instant('Investment ID'),
                hide: true,
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                rowDrag: false,
                resizable: false,
                sortable: false,
              },
              {
                field: 'investment_label',
                headerName: this.translate.instant('core.utils.spreadsheet_columns.column_label'),
                rowDrag: false,
                filter: true,
                lockPosition: true,
                pinned: true,
                cellRenderer: (params: any) => {
                  const value = params.value || '';
                  const linked_to = params.data?.linked_to;
                  const linked_invests_total = params.data?.linked_invests_total ?? 0;
                  const isChild = !!linked_to;
                  const isMain = !linked_to && linked_invests_total > 0;
                  if (isMain) {
                    return `<span class="invest-label-role invest-label-role--main"><img src="/assets/icons/LinkIn.svg" alt="LinkFrom" style="width: 12px"/></span>${value}`;
                  }
                  if (isChild) {
                    return `<span class="invest-label-role invest-label-role--child"><img src="/assets/icons/LinkOut.svg" alt="LinkFrom" style="width: 12px"/></span>${value}`;
                  }
                  return value;
                },
              },
            ]
          },
          // SITE
          {
            headerName: this.translate.instant('core.utils.spreadsheet_columns.column_site'),
            marryChildren: true,
            headerClass: 'hide-ag-header-label',
            children: [
              {
                field: 'site',
                headerName: this.translate.instant('core.utils.spreadsheet_columns.column_site'),
                width: 115,
                refData: this.sitesMap,
                sort: 'asc',
              },
            ]
          },
          // BUDGET / AMOUNT
          {
            headerName: this.translate.instant('Amount'),
            marryChildren: true,
            headerClass: 'hide-ag-header-label',
            children: [
              {
                field: 'budget',
                headerName: this.translate.instant(
                  'Amount',
                ),
                hide: false,
                valueFormatter: this.priceFormatter,
                filter: 'agNumberColumnFilter',
                width: 100,
              }
            ]
          },
          // LINKED INVEST
          {
            headerName: this.translate.instant('Linked invests'),
            marryChildren: true,
            headerClass: 'hide-ag-header-label',
            children: [
              {
                field: 'linked_invests_total',
                headerName: this.translate.instant('Linked invests'),
                headerTooltip: this.translate.instant('Linked invests'),
                headerComponent: 'linkedInvestmentsHeader',
                hide: false,
                width: 80,
                filter: 'agNumberColumnFilter',
                resizable: false,
                valueFormatter: (params) => {
                  if (params.value == null || params.value === 0) {
                    return '-';
                  }
                  return params.value;
                }
              }
            ]
          },
          // MAIN SCENARIO
          {
            headerName: mainScenario.name,
            hide: hideMainScenario,
            suppressColumnsToolPanel: hideMainScenario,
            suppressFiltersToolPanel: hideMainScenario,
            marryChildren: true,
            icons: {
              columnGroupOpened: '<i class="far fa-minus"/>',
              columnGroupClosed: '<i class="far fa-plus"/>',
            },
            children: [
              {
                field: `empty_scenario_${mainScenario.id}`,
                headerName: '',
                width: 115,
                hide: true,
                lockVisible: true,
                columnGroupShow: 'closed',
                menuTabs: [],
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                filter: false,
              },
              {
                field: `year_scenario_${mainScenario.id}`,
                headerName: this.translate.instant(
                  'core.utils.spreadsheet_columns.column_year',
                ),
                width: 100,
                availableFilterField: 'investment_year',
                notNull: true,
                hide: hideMainScenario,
                suppressColumnsToolPanel: hideMainScenario,
                suppressFiltersToolPanel: hideMainScenario,
                columnGroupShow: 'open',
              },
              {
                field: `status_scenario_${mainScenario.id}`,
                headerName: this.translate.instant(
                  'core.utils.spreadsheet_columns.column_investment_status',
                ),
                width: 130,
                refData: this.investmentStatusesMap,
                availableFilterField: 'investment_status',
                notNull: true,
                hide: hideMainScenario,
                suppressColumnsToolPanel: hideMainScenario,
                suppressFiltersToolPanel: hideMainScenario,
                columnGroupShow: 'open',
              },
            ],
          },
          // FORCED SCENARIO
          ...(
            forcedScenario
              ? [forcedScenario]
                  .filter((s) => !s.is_main)
                  .map<KPI.CustomColGroupDef>((scenario) => ({
                    headerName: scenario.name,
                    headerClass: `scenario-${scenario.id}`,
                    marryChildren: true,
                    icons: {
                      columnGroupOpened: '<i class="far fa-minus"/>',
                      columnGroupClosed: '<i class="far fa-plus"/>',
                    },
                    children: [
                      {
                        field: `empty_scenario_${scenario.id}`,
                        headerName: '',
                        width: 115,
                        hide: true,
                        lockVisible: true,
                        columnGroupShow: 'closed' as const,
                        menuTabs: [],
                        suppressColumnsToolPanel: true,
                        suppressFiltersToolPanel: true,
                        filter: false,
                      },
                      {
                        field: `year_scenario_${scenario.id}`,
                        headerName: this.translate.instant(
                          'core.utils.spreadsheet_columns.column_year'
                        ),
                        width: 100,
                        availableFilterField: 'investment_year',
                        notNull: true,
                        cellClass: `scenario-${scenario.id}`,
                        headerClass: `scenario-${scenario.id}`,
                        columnGroupShow: 'open' as const,
                      },
                      {
                        field: `status_scenario_${scenario.id}`,
                        headerName: this.translate.instant(
                          'core.utils.spreadsheet_columns.column_investment_status'
                        ),
                        width: 130,
                        refData: this.investmentStatusesMap,
                        cellClass: `scenario-${scenario.id}`,
                        headerClass: `scenario-${scenario.id}`,
                        availableFilterField: 'investment_status',
                        columnGroupShow: 'open' as const,
                        notNull: true,
                      },
                    ],
                  }))
              : [] as KPI.CustomColGroupDef[]
          ),
          // ALT SCENARII
          ...(
            showScenarii && !forcedScenario
              ? selectedScenarii
                  .filter((s) => !s.is_main)
                  .map<KPI.CustomColGroupDef>((scenario) => ({
                    headerName: scenario.name,
                    marryChildren: true,
                    headerClass: `scenario-${scenario.id}`,
                    icons: {
                      columnGroupOpened: '<i class="far fa-minus"/>',
                      columnGroupClosed: '<i class="far fa-plus"/>',
                    },
                    children: [
                      {
                        field: `empty_scenario_${scenario.id}`,
                        headerName: '',
                        width: 115,
                        hide: true,
                        lockVisible: true,
                        columnGroupShow: 'closed' as const,
                        menuTabs: [],
                        suppressColumnsToolPanel: true,
                        suppressFiltersToolPanel: true,
                        filter: false,
                      },
                      {
                        field: `year_scenario_${scenario.id}`,
                        headerName: this.translate.instant(
                          'core.utils.spreadsheet_columns.column_year'
                        ),
                        width: 100,
                        availableFilterField: 'investment_year',
                        notNull: true,
                        valueFormatter: this.nullFormatter,
                        cellClass: `scenario-${scenario.id}`,
                        columnGroupShow: 'open' as const,
                        headerClass: `scenario-${scenario.id}`,
                      },
                      {
                        field: `status_scenario_${scenario.id}`,
                        headerName: this.translate.instant(
                          'core.utils.spreadsheet_columns.column_investment_status'
                        ),
                        width: 130,
                        refData: this.investmentStatusesMap,
                        cellClass: `scenario-${scenario.id}`,
                        headerClass: `scenario-${scenario.id}`,
                        columnGroupShow: 'open' as const,
                        availableFilterField: 'investment_status',
                        notNull: true,
                      },
                    ],
                  }))
              : [] as KPI.CustomColGroupDef[]
          ),
          // PERIMETER
          {
            headerName: this.translate.instant('Perimeter'),
            marryChildren: true,
            hide: false,
            icons: {
              columnGroupOpened: '<i class="far fa-minus"/>',
              columnGroupClosed: '<i class="far fa-plus"/>',
            },
            children: [
              {
                field: 'empty_perimeter_info',
                headerName: '',
                width: 100,
                hide: true,
                lockVisible: true,
                columnGroupShow: 'closed',
                menuTabs: [],
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                filter: false,
              },
              {
                field: 'perimeter_type',
                headerName: this.translate.instant('Child perimeter type'),
                refData: this.perimeterTypesMap,
                notNull: true,
                columnGroupShow: 'open',
              },
              {
                field: 'perimeter_id',
                headerName: this.translate.instant('Perimeter name'),
                hide: false,
                width: 200,
                refData: this.perimetersMap,
                notNull: true,
                columnGroupShow: 'open',
              },
              ...(this.settingService.settingMustBeTrue('PERIMETER_CODE_COLUMN_ENABLE') ? [{
                field: 'perimeter_code',
                headerName: this.translate.instant('Perimeter code'),
                hide: true,
              }] : []),
              {
                field: 'cluster',
                headerName: this.translate.instant('Site cluster'),
                refData: this.clustersMap,
                columnGroupShow: 'open',
                notNull: true,
              },
            ]
          },
          // COMPLEMENTARY INFOS
          {
            headerName: this.translate.instant('Detail'),
            marryChildren: true,
            icons: {
              columnGroupOpened: '<i class="far fa-minus"/>',
              columnGroupClosed: '<i class="far fa-plus"/>',
            },
            hide: false,
            children: [
              {
                field: 'empty_info',
                headerName: '',
                width: 80,
                hide: true,
                lockVisible: true,
                columnGroupShow: 'closed',
                menuTabs: [],
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                filter: false,
              },
              {
                field: 'priority',
                headerName: this.translate.instant(
                  'core.utils.spreadsheet_columns.column_priority',
                ),
                width: 90,
                refData: this.prioritiesMap,
                columnGroupShow: 'open',
              },
              {
                field: 'budget_origin',
                headerName: this.translate.instant('Budget origin'),
                width: 130,
                columnGroupShow: 'open',
                refData: this.budgetOriginsMap,
              },
              {
                field: 'investment_type',
                headerName: this.translate.instant('core.utils.spreadsheet_columns.column_investment_type'),
                refData: this.investmentTypesMap,
                notNull: true,
                columnGroupShow: 'open',
              },
              {
                field: 'further_information',
                headerName: this.translate.instant('core.utils.spreadsheet_columns.column_further_information'),
                refData: this.furtherInformationsMap,
                valueFormatter: (params) => {
                  if (params.value === null || params.value === undefined || params.value === '') {
                    return '';
                  }
                  return this.furtherInformationsMap[params.value] ?? params.value;
                },
                columnGroupShow: 'open',
              },
              {
                field: 'has_taxonomy_objective',
                headerName: this.translate.instant('Aligned CAPEX'),
                width: 130,
                refData: this.trueFalseMap,
                columnGroupShow: 'open',
                filterParams: {
                  values: (params) => {
                    params.success([true, false]);
                  },
                },
              },
              {
                field: 'has_invoicing',
                headerName: this.translate.instant('pages.common.ag-grid.column_groups.invoice'),
                width: 130,
                refData: this.trueFalseToBeDefined,
                columnGroupShow: 'open',
                filterParams: {
                  values: (params) => {
                    params.success([true, false, null]);
                  },
                },
              },
              // {
              //   field: 'additional_information_execution',
              //   headerName: this.translate.instant(
              //     'core.utils.spreadsheet_columns.column_additional_information_execution',
              //   ),
              //   width: 130,
              //   columnGroupShow: 'open',
              // },
              // {
              //   field: 'has_initiatives',
              //   headerName: this.translate.instant(
              //     'core.utils.spreadsheet_columns.column_initiative',
              //   ),
              //   suppressColumnsToolPanel: !can_access_energy,
              //   suppressFiltersToolPanel: !can_access_energy,
              //   width: 100,
              //   refData: this.trueFalseMap,
              //   columnGroupShow: 'open',
              //   quickFilter: can_access_energy,
              //   filterParams: {
              //     values: (params) => {
              //       params.success([true, false]);
              //     },
              //   },
              // },
              {
                field: 'comments',
                headerName: this.translate.instant(
                  'core.utils.spreadsheet_columns.column_comments',
                ),
                cellEditor: 'agLargeTextCellEditor',
                hide: false,
                columnGroupShow: 'open',
                width: 130,
                filterParams: {
                  valueFormatter: (params) => {
                    return params.value?.replace(/\n/g, ' ');
                  },
                },
              },
            ]
          },
          // LINKED ASSET
          {
            headerName: this.translate.instant('Equipment'),
            marryChildren: true,
            hide: isFidjiAct,
            suppressColumnsToolPanel: isFidjiAct,
            suppressFiltersToolPanel: isFidjiAct,
            icons: {
              columnGroupOpened: '<i class="far fa-minus"/>',
              columnGroupClosed: '<i class="far fa-plus"/>',
            },
            children: [
              {
                field: 'empty_linked_asset',
                headerName: '',
                width: 115,
                hide: true,
                lockVisible: true,
                columnGroupShow: 'closed',
                menuTabs: [],
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                filter: false,
              },
              {
                field: 'asset_label',
                headerName: this.translate.instant('Label'),
                width: 150,
                valueFormatter: params => params.value?.label,
                keyCreator: params => params.value?.label ?? params.value,
                filterParams: {
                  valueFormatter: params => params.value?.label ?? params.value,
                },
                columnGroupShow: 'open',
              },
              {
                field: 'asset_type',
                headerName: this.translate.instant('pages.admin.nomenclature.column_asset_type'),
                refData: this.assetTypesMap,
                availableFilterField: 'investment_asset_type',
                columnGroupShow: 'open',
              },
              {
                field: 'asset_id',
                headerName: this.translate.instant('core.utils.spreadsheet_columns.column_asset_id'),
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
              },
              {
                field: 'asset_quantity',
                headerName: this.translate.instant('core.utils.spreadsheet_columns.column_asset_quantity'),
                filter: 'agNumberColumnFilter',
                width: 150,
                columnGroupShow: 'open',
              },
              {
                field: 'asset_eol',
                headerName: this.translate.instant("Theoretical end of life"),
                filter: 'agNumberColumnFilter',
                width: 150,
                columnGroupShow: 'open',
              },
              {
                field: 'asset_last_evaluation',
                headerName: this.translate.instant('Last technical evaluation'),
                columnGroupShow: 'open',
                width: 150,
                refData: this.technicalEvaluationMap,
                filter: 'agSetColumnFilter',
                filterParams: {
                  values: (params) => {
                    params.success([null, 0, 1, 2, 3]);
                  },
                  refreshValuesOnOpen: false,
                  suppressClearModelOnRefreshValues: true,
                },
                cellRenderer: 'lastTechnicalStateRenderer',
                cellRendererParams: {
                  showWithoutNotation: false,
                },
              },
              {
                field: 'has_technical_impact',
                headerName: this.translate.instant('Impact on the technical state'),
                width: 130,
                refData: this.trueFalseMap,
                columnGroupShow: 'open',
                filterParams: {
                  values: (params) => {
                    params.success([true, false]);
                  },
                },
              },
            ],
          },
          // TAGS
          {
            headerName: this.translate.instant('pages.common.ag-grid.column_groups.tag'),
            headerClass: 'justify-center',
            marryChildren: true,
            hide: false,
            icons: {
              columnGroupOpened: '<i class="far fa-minus"/>',
              columnGroupClosed: '<i class="far fa-plus"/>',
            },
            children: [
               {
                field: 'empty_tags',
                headerName: '',
                width: 85,
                hide: true,
                lockVisible: true,
                columnGroupShow: 'closed',
                menuTabs: [],
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                filter: false,
              },
              ...(invReasonsCols as KPI.CustomColDef[]).map((col) => ({
                ...col,
                columnGroupShow: 'open' as ColumnGroupShowType,
              })),
            ],
          },
          // IMPACTS
          {
            headerName: this.translate.instant('pages.insight.investment.impact'),
            headerClass: 'justify-center',
            hide: false,
            groupId: "impacts",
            marryChildren: true,
            icons: {
              columnGroupOpened: '<i class="far fa-minus"/>',
              columnGroupClosed: '<i class="far fa-plus"/>',
            },
            children: [
              {
                field: 'empty_impact',
                headerName: '',
                width: 85,
                hide: true,
                lockVisible: true,
                columnGroupShow: 'closed',
                menuTabs: [],
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                filter: false,
              },
              ...(roadmapIndicatorsCols as KPI.CustomColDef[]),
            ],
          },
          ...(can_access_energy ? [{
            headerName: this.translate.instant("Desired gains"),
            headerClass: 'justify-center',
            hide: false,
            groupId: "desired_gains",
            marryChildren: true,
            icons: {
              columnGroupOpened: '<i class="far fa-minus"/>',
              columnGroupClosed: '<i class="far fa-plus"/>',
            },
            children: [
              {
                field: 'empty_desired_gain',
                headerName: '',
                width: 85,
                hide: true,
                lockVisible: true,
                columnGroupShow: 'closed' as const,
                menuTabs: [],
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                filter: false,
              },
              {
                field: `gain_all`,
                headerName: this.translate.instant("All energy"),
                valueFormatter: this.kwhFormatter,
                cellRenderer: 'evolutionRenderer',
                hide: false,
                width: 150,
                menuTabs: [],
                minWidth: 100,
                filter: 'agNumberColumnFilter',
                columnGroupShow: 'open' as ColumnGroupShowType,
              } as KPI.CustomColDef,
              ...energyTypes.map((energyType): KPI.CustomColDef => ({
                field: `gain_${energyType.id}`,
                headerName: energyType.name,
                hide: !energyType.is_electricity,
                width: Math.min(
                  Math.max((energyType.name.length * 8 + 60), 140),
                  240,
                ),
                valueFormatter: this.kwhFormatter,
                cellRenderer: 'evolutionRenderer',
                filter: 'agNumberColumnFilter',
                menuTabs: [],
                columnGroupShow: 'open' as ColumnGroupShowType,
                headerClass: `energy-type-${energyType.id}`,
                minWidth: 100,
              })),
              {
                field: 'greenhouse_gas_savings_full_year',
                headerName: this.translate.instant('pages.common.ag-grid.initiatives_table.GHG_emissions_avoided_first_year'),
                valueFormatter: (params) => {
                  const val = params.value != null ? Math.floor(params.value) : params.value;
                  return this.ghgFormatter({ ...params, value: val });
                },
                hide: false,
                width: 150,
                minWidth: 100,
                sortable: false,
                filter: false,
                menuTabs: [],
                suppressFiltersToolPanel: true,
                columnGroupShow: 'open' as ColumnGroupShowType,
              } as KPI.CustomColDef,
            ]
          }] : []),
          // FIDJI ACT
          ...(!this.settingService.settingMustBeTrue('ENERGY__ENABLE') ? [] : [{
            headerName: this.translate.instant('Fidji ACT short label'),
            headerClass: 'hide-ag-header-label',
            marryChildren: true,
            icons: {
              columnGroupOpened: '<i class="far fa-minus"/>',
              columnGroupClosed: '<i class="far fa-plus"/>',
            },
            children: [
              {
                field: 'fidji_act_short_label',
                headerName: this.translate.instant('Fidji ACT short label'),
                width: 200,
                minWidth: 150,
                refData: this.initiativeTypesLeafFriendlyMap,
                valueFormatter: (params) => {
                  if (params.value === null || params.value === undefined || params.value === '') {
                    return '-';
                  }
                  return this.initiativeTypesLeafFriendlyMap[params.value] ?? params.value;
                },
              }
            ]
          }]),
          // Sponsors OR Quote-part
          ...sponsorsOrQuotePart,
          // CODES
          {
            headerName: this.translate.instant('Codes'),
            marryChildren: true,
            hide: false,
            icons: {
              columnGroupOpened: '<i class="far fa-minus"/>',
              columnGroupClosed: '<i class="far fa-plus"/>',
            },
            children: [
              {
                field: 'empty_code',
                headerName: '',
                width: 85,
                hide: true,
                lockVisible: true,
                columnGroupShow: 'closed',
                menuTabs: [],
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                filter: false,
              },
              ...codesCols.map((col) => ({
                ...col,
                columnGroupShow: 'open' as ColumnGroupShowType,
              })),
            ],
          },
          // BUDGET CATEGORY
          {
            headerName: this.translate.instant('core.utils.spreadsheet_columns.column_budget_category_level_1'),
            marryChildren: true,
            hide: false,
            icons: {
              columnGroupOpened: '<i class="far fa-minus"/>',
              columnGroupClosed: '<i class="far fa-plus"/>',
            },
            children: [
              {
                field: 'empty_budget_category',
                headerName: '',
                width: 135,
                hide: true,
                lockVisible: true,
                columnGroupShow: 'closed',
                menuTabs: [],
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                filter: false,
              },
              ...(invCategCols as KPI.CustomColDef[])
            ]
          },
          // TECHNICAL NOMENCLATURE
          {
            headerName: this.translate.instant('Technical nomenclature'),
            marryChildren: true,
            hide: false,
            icons: {
              columnGroupOpened: '<i class="far fa-minus"/>',
              columnGroupClosed: '<i class="far fa-plus"/>',
            },
            children: [
              {
                field: 'empty_nomenclature',
                headerName: '',
                width: 165,
                hide: true,
                lockVisible: true,
                columnGroupShow: 'closed',
                menuTabs: [],
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                filter: false,
              },
              {
                field: 'super_category',
                headerName: this.translate.instant('pages.admin.nomenclature.column_super_category'),
                refData: this.superCategoriesMap,
                quickFilter: true,
                availableFilterField: 'investment_super_category',
                columnGroupShow: 'open',
              },
              {
                field: 'category',
                headerName: this.translate.instant('pages.admin.nomenclature.column_category'),
                refData: this.categoriesMap,
                availableFilterField: 'investment_category',
                columnGroupShow: 'open',
              },
              {
                field: 'sub_category',
                headerName: this.translate.instant('core.utils.spreadsheet_columns.column_sub_category'),
                refData: this.subCategoriesMap,
                availableFilterField: 'investment_sub_category',
                columnGroupShow: 'open',
              },
            ]
          },
          // DOCUMENTS
          {
            headerName: this.translate.instant('pages.common.ag-grid.column_groups.document'),
            hide: false,
            icons: {
              columnGroupOpened: '<i class="far fa-minus"/>',
              columnGroupClosed: '<i class="far fa-plus"/>',
            },
            children: [
              {
                field: 'empty_doc',
                headerName: '',
                width: 130,
                hide: true,
                lockVisible: true,
                columnGroupShow: 'closed',
                menuTabs: [],
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                filter: false,
              },
              ...(docTypesCols as KPI.CustomColDef[])
            ],
          },
          this.getAgGridImportColumnGroup(),
        ];
      })
    );
  }

  public getAgGridInvestmentColumns(
    showScenarii: boolean,
    forcedScenario: KPI.InvestmentScenario = null,
    isFidjiAct: boolean = false,
  ): Observable<KPI.CustomColGroupDef[]> {
    return combineLatest([
      this.agGridInvestmentColumns(showScenarii, forcedScenario, isFidjiAct),
      this.configService.investmentDefaultColumns$.pipe(startWith([])),
    ]).pipe(
      map(([agGridInvestmentColumns, defaultColumns]) => {
        const columnsToHide = [
          'invest_id',
          'asset_id',
          'perimeter_code',
        ];

        if (defaultColumns?.length) {
          agGridInvestmentColumns = agGridInvestmentColumns.map((columnGroup) => ({
            ...columnGroup,
            children: columnGroup.children.map((column: KPI.CustomColDef) => ({
              ...column,
              hide: !!columnsToHide.find((colToHide) => column.field.startsWith(colToHide)),
            }))
          }));
        }

        /** Return the generated columns, mapped with the saved columns state from local storage
         *
         *   Hide column priority of applying :
         *     1. columns parameter from URL
         *     2. columns state from local storage
         *     3. columns hardcoded state
         */
        return agGridInvestmentColumns;
      }),
    );
  }

  public getInvestmentsData(options: any): Observable<any> {
    if (options.filterModel && options.filterModel.invest_id && options.filterModel.invest_id.values) {
      options.InvestmentId = options.filterModel.invest_id.values;
    }

    if (options.filterModel && options.filterModel.gtp_code && options.filterModel.gtp_code.values) {
      const transformedValues = options.filterModel.gtp_code.values.map((value: string) => {
        if (value.includes('_')) {
          return value.split('_').pop();
        }
        return value;
      });
      options = {
        ...options,
        filterModel: {
          ...options.filterModel,
          gtp_code: {
            ...options.filterModel.gtp_code,
            values: transformedValues
          }
        }
      };
    }

    if (options.filterModel?.asset_last_evaluation?.values) {
      options.filterModel.asset_last_evaluation.values =
        options.filterModel.asset_last_evaluation.values.map(v => v !== null ? Number(v) : null);
    }

    if (options.filterModel && options.filterModel.fidji_act_short_label) {
      const filterValue = options.filterModel.fidji_act_short_label;
      options = {
        ...options,
        filterModel: {
          ...options.filterModel,
          initiative_type_3: filterValue
        }
      };
      delete options.filterModel.fidji_act_short_label;
    }

    return combineLatest([
      this.scope.referenceFields$.pipe(idMapToArray()),
      this.referenceDataService.perimeterReferenceDataMaps$,
      this.api.investments_table(options),
      this.scope.investmentSponsors$.pipe(idMapToArray()),
      this.scope.investmentInvoicesRecipient$.pipe(idMapToArray()),
      this.investmentReasonsService.investmentReasons$,
      this.documentTypesService.documentTypes$.pipe(idMapToArray()),
      this.roadmapService.roadmapIndicators().pipe(first()),
    ]).pipe(
      tap(() => this.currentInvestmentTableOptions.next(options)),
      map(
        ([
          referenceFields,
          perimeters,
          data,
          investmentSponsors,
          investmentInvoicesRecipient,
          investmentReasons,
          documentTypes,
          roadmapIndicators,
        ]) => ({
          ...data,
          rowData: (data?.rowData || []).map((row) => {
            // REFERENCE FIELDS
            const site = (perimeters || []).find((p) => p.id === row.site);
            if (!!site?.ref_data_map) {
              referenceFields.map((referenceField) => {
                if (
                  referenceField.displayed_intelligence &&
                  site.ref_data_map[referenceField.var_name]
                ) {
                  row[`reference_field_${referenceField.id}`] =
                    site.ref_data_map[referenceField.var_name].value;
                }
              });
            }

            // PAYERS
            (investmentSponsors || []).map((sponsor) => {
              const sponsorObject = row.sponsorships
                ? row.sponsorships.find((s) => s.sponsor === sponsor.id)
                : null;
              if (sponsorObject) {
                row[`sponsorships_${sponsor.id}`] =
                  (row.budget * +sponsorObject.percentage) / 100;
              } else {
                row[`sponsorships_${sponsor.id}`] = 0;
              }
            });

            // INVOICING
            (investmentInvoicesRecipient || []).map((invoicing) => {
              const sponsorObject = row.invoicings
                ? row.invoicings.find((s) => s.invoicing === invoicing.id)
                : null;
              if (sponsorObject) {
                row[`invoicings_${invoicing.id}`] =
                  (row.budget * +sponsorObject.percentage) / 100;
              } else {
                row[`invoicings_${invoicing.id}`] = 0;
              }
            });

            // TAGS
            (investmentReasons || []).map((investmentReason) => {
              row[`investment_reason_${investmentReason.id}`] =
                row.reasons && row.reasons.length
                  ? row.reasons.find((r) => r.id === investmentReason.id)
                    ? true
                    : false
                  : false;
            });

            // DOCUMENTS
            (documentTypes || []).map((docType) => {
              const doc =
                row.documents && row.documents.length
                  ? row.documents.find((d) => d.document_type === docType.id)
                  : null;

              row[`document_${docType.id}`] = !!doc
            });

            // IMPACT ROADMAP INDICATORS
            (roadmapIndicators || []).map((indicator) => {
              row[`impact_roadmap_indicators_${indicator.id}`] =
                row.impact_roadmap_indicators?.length
                  ? !!row.impact_roadmap_indicators.find((i) => i.id === indicator.id)
                  : false;
            });

            if (row.gains && row.gains.length > 0) {
              row['gain_all'] = row.gains.reduce((acc, gain) => acc + (gain.theoretical_savings_full_year || 0), 0);
              row.gains.map((gain) => {
                row[`gain_${gain.energy_type_id}`] = gain.theoretical_savings_full_year || 0;
              });
            } else {
              row['gain_all'] = 0;
            }

            row.invest_id = row.id;
            row["scenarii_data"].map((i) => {
              row[`year_scenario_${i.scenario_id}`] = i.final_schedule === i.final_schedule_to
                ? i.final_schedule_to : `${i.final_schedule} - ${i.final_schedule_to}`;
              row[`id_scenario_${i.scenario_id}`] = i.id;
              row[`status_scenario_${i.scenario_id}`] = i.status;
            });
            // QUOTE_PART
            row['quote_part'] = row.quote_part;
            for (let i = 0; i < row.sponsorships?.length; i++) {
              if (!row[`sponsorships_${row.sponsorships[i].sponsor_id}`]) {
                row[`sponsorships_${row.sponsorships[i].sponsor_id}`] = 0;
              }
              row[`sponsorships_${row.sponsorships[i].sponsor_id}`] += row.sponsorships[i].amount;
            }
            row['refacturation'] = row.refacturation;

            return row;
          }),
        }),
      ),
    );
  }

  /**
   * Builds status filter for investments table, to be sent through URL
   *
   * @param statusAttributes: KPI.InvestmentStatusAttribute[] - List of status sttributes to filter on
   * @param value: boolean - Value of the status attributes to filter on
   * @returns string
   *
   * Example: buildStatusFilter(['hypothesis'], true)
   *          => '1,2'
   */
  public buildStatusFilter(
    statusAttributes: KPI.InvestmentStatusAttribute[],
    value: boolean
  ): Observable<number[]> {
    return this.scope.investmentStatuses$.pipe(
      idMapToArray(),
      map((statuses) =>
        statuses.filter((status) =>
          statusAttributes.some((attr) => status[attr] === value),
        ).map((status) => status.id),
      ),
    );
  }

  public get plannedStatusFilter$(): Observable<number[]> {
    return this.buildStatusFilter(['is_request', 'is_proposed', 'is_validated', 'is_closed', 'is_engaged'], true);
  }

  public get hypothesisStatusFilter$(): Observable<number[]> {
    return this.buildStatusFilter(['hypothesis'], true);
  }

  public preExportInvestments(columnDefs: ColDef<any, any>[]) {
    return this.api.pre_download_investments_excel_export({ columnDefs, payload: this.currentInvestmentTableOptions.value })
  }

  public exportInvestments(columnDefs: ColDef<any, any>[]) {
    return this.exportsService.download_investments_excel_export({ columnDefs, payload: this.currentInvestmentTableOptions.value })
  }
}
